import React, { useState, useEffect } from 'react';
import AuthService from '../services/AuthService';
import { useNavigate, NavLink, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from "../services/ApiService";
import Counter from "../components/Counter";
import { formatCurrency, formatDate } from "../utils/helper";
import { getAppointmentStatus } from "../utils/statusHelper";

const Dashboard = () => {
    const navigate = useNavigate();
    
    const [isLoader, setIsLoader] = useState(false);
    const [stats, setStats] = useState({});
    const [recentAppointments, setRecentAppointments] = useState([]);
    
    const [current_month, setCurrentMonth] = useState("");
    const currency = process.env.REACT_APP_CURRENCY || 'AED';

    const authUser = AuthService.getCurrentUser();
    const isCustomer = authUser && authUser.role === 'customer';
    const isAdmin = authUser && (authUser.role === 'admin' || authUser.role === 'staff');

    useEffect(() => {
        const isAuthenticated = AuthService.getCurrentUser();
        if (!isAuthenticated) {
            navigate('/login');
        } else if (isAuthenticated.role === 'customer') {
            navigate('/appointments');
            return;
        } else {
            getDashboard();
        }

        const now = new Date();
        const formattedDate = formatDate(now, "MMM, YYYY");
        setCurrentMonth(formattedDate);
    }, [navigate]);


    const getDashboard = async () => {
        try {
            setIsLoader(true);
            const url = isCustomer ? '/customer/dashboard' : '/dashboard';
            const response = await ApiService.request({
                method: "GET",
                url: url,
            });
            const data = response.data;
            if (data.status) {
                setStats(data.data.stats);
                setRecentAppointments(data.data.recent_appointments || []);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            console.error('Dashboard error:', error);
        }
    };


  return (
    <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                    <h1 className="page-heading d text-dark fw-bold fs-3  my-0">
                        Welcome, {authUser.name}
                         <span className="badge bg-base2 ms-3 text-uppercase">{authUser.role}</span>  
                    </h1>
                    <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                        <li className="breadcrumb-item text-muted">
                            <Link to="/admin/dashboard" className="text-muted text-hover-primary"> Home </Link>
                        </li>
                        <li className="breadcrumb-item">
                            <span className="bullet bg-gray-400 w-5px h-2px"></span>
                        </li>
                        <li className="breadcrumb-item text-muted"> Dashboard </li>
                    </ul>
                </div>
                <div className="d-flex gap-3">
                    <div className="card border shadow h-100 py-3 px-5 hover-scale">
                        <div className="card-body p-0 d-flex align-items-center gap-3">
                    <Link to="/appointments/create" className="btn btn-base d-flex align-items-center">
                        <i className="fas fa-calendar-plus fs-2 me-2"></i> Book Appointment
                    </Link>
                </div>
                    </div>

                </div>
            </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-fluid">
                {/* Row 1: Key Performance Indicators */}
                <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                    {isAdmin && (
                    <>
                        {/* Total Customers */}
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/customers" className="card border shadow h-100 py-2 hover-scale bg-light-primary">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Customers</div>
                                            <div className="h1 mb-0 fw-bold text-primary">
                                                <Counter value={stats?.total_customers || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Registered customers</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-users fs-3x text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>

                        {/* Total Staff */}
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/users?role=staff" className="card border shadow h-100 py-2 hover-scale bg-light-info">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Staff</div>
                                            <div className="h1 mb-0 fw-bold text-info">
                                                <Counter value={stats?.total_staff || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Active staff</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-user-tie fs-3x text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>
                    </>
                    )}

                    {isCustomer && (
                        <>
                        {/* Total Appointments */}
                        <div className="col-xl-4 col-md-6 mb-4">
                            <NavLink to="/appointments" className="card border shadow h-100 py-2 hover-scale bg-light-primary">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Bookings</div>
                                            <div className="h1 mb-0 fw-bold text-primary">
                                                <Counter value={stats?.total_appointments || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">All time bookings</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-calendar-alt fs-3x text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>
                        {/* Completed Appointments */}
                         <div className="col-xl-4 col-md-6 mb-4">
                            <NavLink to="/appointments?status=completed" className="card border shadow h-100 py-2 hover-scale bg-light-success">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Completed</div>
                                            <div className="h1 mb-0 fw-bold text-success">
                                                <Counter value={stats?.completed_appointments || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Completed visits</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-check-circle fs-3x text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>
                        </>
                    )}

                    {/* Upcoming Appointments */}
                    <div className={`col-xl-${isCustomer ? '4' : '3'} col-md-6 mb-4`}>
                        <NavLink to="/appointments?status=upcoming" className="card border shadow h-100 py-2 hover-scale bg-light-warning">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Upcoming</div>
                                        <div className="h1 mb-0 fw-bold text-warning">
                                            <Counter value={isCustomer ? stats?.upcoming_appointments : stats?.total_upcoming_appointments || 0} duration={1500} />
                                        </div>
                                        <small className="text-muted">Scheduled visits</small>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-clock fs-3x text-warning"></i>
                                    </div>
                                </div>
                            </div>
                        </NavLink>
                    </div>
                    
                    {isAdmin && (
                    <>
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/appointments" className="card border shadow h-100 py-2 hover-scale bg-light-success">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Appointments</div>
                                            <div className="h1 mb-0 fw-bold text-success">
                                                <Counter value={stats?.total_appointments || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Total bookings</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-calendar-check fs-3x text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>

                        {/* Total Sales */}
                        <div className="col-xl-4 col-md-6 mb-4">
                            <div className="card border shadow h-100 py-2 hover-scale bg-light-success">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Sales</div>
                                            <div className="h1 mb-0 fw-bold text-success">
                                                {formatCurrency(stats?.total_sales || 0)}
                                            </div>
                                            <small className="text-muted">Total revenue from appointments</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-dollar-sign fs-3x text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* This Month Sales */}
                        <div className="col-xl-4 col-md-6 mb-4">
                            <div className="card border shadow h-100 py-2 hover-scale bg-light-info">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">This Month Sales</div>
                                            <div className="h1 mb-0 fw-bold text-info">
                                                {formatCurrency(stats?.this_month_sales || 0)}
                                            </div>
                                            <small className="text-muted">Revenue this month</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-chart-line fs-3x text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                    )}

                </div>

                {/* Recent Tables */}
                <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                    {/* Recent Appointments */}
                    <div className="col-xl-6 mb-4">
                        <div className="card border shadow h-100">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bold fs-3 mb-1">Recent Appointments</span>
                                    <span className="text-muted mt-1 fw-semibold fs-7">Latest 5 bookings</span>
                                </h3>
                                <div className="card-toolbar">
                                    <Link to="/appointments" className="btn btn-sm btn-light-primary">View All</Link>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                <div className="table-responsive">
                                    <table className="table align-middle gs-0 gy-4">
                                        <thead>
                                            <tr className="fw-bold text-muted bg-light">
                                                {isAdmin && <th className="ps-4 min-w-100px rounded-start">Customer</th>}
                                                <th className="min-w-100px">Service</th>
                                                <th className="min-w-100px">Date/Time</th>
                                                <th className="min-w-50px rounded-end">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentAppointments.map((appt, index) => (
                                                <tr key={index}>
                                                    {isAdmin && (
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center">
                                                            <div className="d-flex justify-content-start flex-column">
                                                                <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">{appt.customer?.name || 'Guest'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    )}
                                                    <td>
                                                        <span className="text-muted fw-semibold d-block fs-7">
                                                            {appt.services && appt.services.length > 0
                                                                ? appt.services.map(s => s.name).join(', ')
                                                                : (appt.service?.name || '-')}
                                                        </span>
                                                        <span className="text-xs text-gray-500">{appt.branch?.name}</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted fw-semibold d-block fs-7">{formatDate(appt.date,'MMM DD, YYYY')}</span>
                                                        <span className="text-muted fw-semibold d-block fs-7">{appt.time}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getAppointmentStatus(appt.status).badgeClass} fs-7 fw-bold`}>{getAppointmentStatus(appt.status).label}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {recentAppointments.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No recent appointments</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default Dashboard;
